// 867 variant of validator.ts's per-transaction content checks: walks a
// parsed 867 (header, Name loops, PTD-detail loops with nested QTY/AMT/CTP/
// REF/DTM, and a CTT/AMT summary) into its flat segment list, then runs the
// shared dictionary-driven element checks against it, plus the structural
// checks specific to an 867 (missing BPT, no Name loops, no PTD lines, a
// missing or mismatched CTT count).

import type {EdiElementValue, EdiSegment} from './tokenizer';
import {
  checkElements,
  type PathedSegment,
  type ValidationFinding,
} from './validator';
import type {ItemLoop867, NameLoop867, Parsed867} from './parser867';

function elementValueAsString(
  value: EdiElementValue | undefined
): string | undefined {
  if (value === undefined) return undefined;
  return Array.isArray(value) ? value.join('') : value;
}

function collectNameLoopSegments867(
  loop: NameLoop867,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: loop.n1, path: `${path}.n1`}];
  if (loop.n2) out.push({segment: loop.n2, path: `${path}.n2`});
  if (loop.n3) out.push({segment: loop.n3, path: `${path}.n3`});
  if (loop.n4) out.push({segment: loop.n4, path: `${path}.n4`});
  if (loop.n5) out.push({segment: loop.n5, path: `${path}.n5`});
  if (loop.n6) out.push({segment: loop.n6, path: `${path}.n6`});
  loop.n9.forEach((s, i) => out.push({segment: s, path: `${path}.n9[${i}]`}));
  loop.refs.forEach((s, i) =>
    out.push({segment: s, path: `${path}.refs[${i}]`})
  );
  if (loop.per) out.push({segment: loop.per, path: `${path}.per`});
  return out;
}

function collectItemSegments867(
  item: ItemLoop867,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: item.ptd, path: `${path}.ptd`}];
  item.qty.forEach((s, i) => out.push({segment: s, path: `${path}.qty[${i}]`}));
  item.amt.forEach((s, i) => out.push({segment: s, path: `${path}.amt[${i}]`}));
  if (item.ctp) out.push({segment: item.ctp, path: `${path}.ctp`});
  item.refs.forEach((s, i) =>
    out.push({segment: s, path: `${path}.refs[${i}]`})
  );
  item.dtm.forEach((s, i) => out.push({segment: s, path: `${path}.dtm[${i}]`}));
  return out;
}

export function collectParsedSegments867(
  parsed: Parsed867,
  basePath: string
): PathedSegment[] {
  const out: PathedSegment[] = [];
  const {header} = parsed;
  const push = (segment: EdiSegment | undefined, suffix: string) => {
    if (segment) out.push({segment, path: `${basePath}.${suffix}`});
  };
  const pushAll = (segments: EdiSegment[], suffix: string) => {
    segments.forEach((s, i) =>
      out.push({segment: s, path: `${basePath}.${suffix}[${i}]`})
    );
  };

  push(header.bpt, 'header.bpt');
  push(header.cur, 'header.cur');
  pushAll(header.refs, 'header.refs');
  pushAll(header.dtm, 'header.dtm');

  parsed.nameLoops.forEach((loop, i) => {
    out.push(
      ...collectNameLoopSegments867(loop, `${basePath}.nameLoops[${i}]`)
    );
  });
  parsed.items.forEach((item, i) => {
    out.push(...collectItemSegments867(item, `${basePath}.items[${i}]`));
  });

  push(parsed.summary.ctt, 'summary.ctt');
  pushAll(parsed.summary.amt, 'summary.amt');

  return out;
}

export function validateParsed867(
  parsed: Parsed867,
  txPath: string
): ValidationFinding[] {
  const findings: ValidationFinding[] = [];

  if (!parsed.header.bpt) {
    findings.push({
      severity: 'error',
      path: `${txPath}.header.bpt`,
      message:
        'Missing BPT segment — every 867 must open with a Beginning segment for Product Transfer and Resale Report.',
    });
  }

  if (parsed.nameLoops.length === 0) {
    findings.push({
      severity: 'error',
      path: `${txPath}.nameLoops`,
      message:
        'No N1 party loops found in this product transfer and resale report.',
    });
  }

  if (parsed.items.length === 0) {
    findings.push({
      severity: 'error',
      path: `${txPath}.items`,
      message:
        'No PTD product transfer lines found in this product transfer and resale report.',
    });
  }

  if (!parsed.summary.ctt) {
    findings.push({
      severity: 'error',
      path: `${txPath}.summary.ctt`,
      message:
        'Missing CTT segment — the transaction has no summary line-item count.',
    });
  } else {
    const declared = elementValueAsString(parsed.summary.ctt.elements[0]);
    if (declared !== undefined && Number(declared) !== parsed.items.length) {
      findings.push({
        severity: 'error',
        path: `${txPath}.summary.ctt`,
        message: `CTT01 says ${declared} line item(s) but ${parsed.items.length} PTD line(s) were found.`,
      });
    }
  }

  for (const {segment, path} of collectParsedSegments867(parsed, txPath)) {
    checkElements(segment, path, findings);
  }

  return findings;
}
